import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../../../../bombiix-icon';
import './NewSidePanel.css';

export interface NewSidePanelProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  width?: number;
  anchorSelector?: string;
  headerLeft?: React.ReactNode;
  className?: string;
  /** Stamps `data-testid` on the panel root so consumers can assert the drawer is mounted. */
  testId?: string;
  children: React.ReactNode;
}

const DEFAULT_WIDTH = 504;
const DEFAULT_ANCHOR = '.page-title-filter';

/**
 * NewSidePanel — Figma-locked right-side drawer shell.
 * Top edge anchored to the bottom of `anchorSelector` (default
 * `.page-title-filter`) so it sits flush below the sticky page header.
 */
function NewSidePanel({
  isOpen,
  title,
  onClose,
  width = DEFAULT_WIDTH,
  anchorSelector = DEFAULT_ANCHOR,
  headerLeft,
  className,
  testId,
  children,
}: NewSidePanelProps) {
  const { t: tRaw } = useTranslation('pim');
  const panelRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.classList.add('new-side-panel-open');
    document.documentElement.classList.add('new-side-panel-open');
    return () => {
      document.body.classList.remove('new-side-panel-open');
      document.documentElement.classList.remove('new-side-panel-open');
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const updatePosition = () => {
      const anchor = document.querySelector(anchorSelector);
      const panel = panelRef.current;
      const backdrop = backdropRef.current;
      if (anchor) {
        const rect = anchor.getBoundingClientRect();
        if (panel) {
          panel.style.top = `${rect.bottom}px`;
          panel.style.height = `calc(100vh - ${rect.bottom}px)`;
        }
        if (backdrop) {
          backdrop.style.top = `${rect.bottom}px`;
          backdrop.style.height = `calc(100vh - ${rect.bottom}px)`;
        }
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, anchorSelector]);

  if (!isOpen) return null;

  const rootClass = ['new-side-panel', className].filter(Boolean).join(' ');
  const backdropClass = [
    'new-side-panel__backdrop',
    className ? `${className}__backdrop` : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        ref={backdropRef}
        className={backdropClass}
        data-testid="new-side-panel-backdrop"
        onClick={onClose}
        role="presentation"
      />
      <div ref={panelRef} className={rootClass} style={{ width: `${width}px`, maxWidth: `${width}px` }} data-testid={testId}>
        <div className="new-side-panel__header">
          {headerLeft ? <div className="new-side-panel__header-left">{headerLeft}</div> : null}
          <h4 className="new-side-panel__title">{title}</h4>
          <button
            type="button"
            data-testid="new-side-panel-close"
            className="new-side-panel__close-btn"
            aria-label={String(tRaw('PIM.COMMON.CLOSE'))}
            onClick={onClose}
          >
            <CloseIcon size={24} />
          </button>
        </div>
        <div className="new-side-panel__body">{children}</div>
      </div>
    </>
  );
}

export default NewSidePanel;
